import { formatMoney } from '../../utils/formatMoney';

export const EMPTY_SUMMARY = {
    grossRevenue: 0,
    cartTotal: 0,
    shipping: 0,
    surcharge: 0,
    profit: 0,
    orderCount: 0,
};

export const REVENUE_PIE_COLORS = {
    'Product total': '#1A1714',
    Shipping: '#6B7C3E',
    Surcharge: '#C4603C',
};

export const formatInputDate = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

export const parseYmd = (ymd) => {
    const [y, m, d] = ymd.split('-').map(Number);
    return new Date(y, m - 1, d);
};

export const daysInclusive = (startStr, endStr) => {
    const start = parseYmd(startStr);
    const end = parseYmd(endStr);
    const ms = end.getTime() - start.getTime();
    return Math.max(1, Math.floor(ms / 86400000) + 1);
};

export const shiftYmd = (ymd, deltaDays) => {
    const d = parseYmd(ymd);
    d.setDate(d.getDate() + deltaDays);
    return formatInputDate(d);
};

export const priorPeriodRange = (startStr, endStr) => {
    const days = daysInclusive(startStr, endStr);
    const priorEnd = shiftYmd(startStr, -1);
    const priorStart = shiftYmd(priorEnd, -(days - 1));
    return { priorStart, priorEnd, days };
};

export const normalizeAnalyticsPayload = (raw, fallbackRange) => {
    if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
        return {
            range: fallbackRange,
            series: [],
            summary: { ...EMPTY_SUMMARY },
            _invalidShape: true,
        };
    }
    if (typeof raw.error === 'string' && raw.error && !raw.summary) {
        return {
            range: raw.range || fallbackRange,
            series: [],
            summary: { ...EMPTY_SUMMARY },
            _apiError: raw.error,
        };
    }
    const summary =
        raw.summary && typeof raw.summary === 'object'
            ? { ...EMPTY_SUMMARY, ...raw.summary }
            : { ...EMPTY_SUMMARY };
    return {
        range:
            raw.range && typeof raw.range === 'object'
                ? { start: raw.range.start, end: raw.range.end }
                : fallbackRange,
        series: Array.isArray(raw.series) ? raw.series : [],
        summary,
    };
};

export const fillDailySeries = (startStr, endStr, series) => {
    const byDate = Object.fromEntries(series.map((s) => [s.date, s]));
    const start = parseYmd(startStr);
    const end = parseYmd(endStr);
    const out = [];
    for (let cur = new Date(start); cur <= end; cur.setDate(cur.getDate() + 1)) {
        const key = formatInputDate(cur);
        out.push(
            byDate[key] ?? {
                date: key,
                cartTotal: 0,
                shipping: 0,
                surcharge: 0,
                grossRevenue: 0,
                profit: 0,
                orderCount: 0,
            }
        );
    }
    return out;
};

export const buildPieRows = (summary) => {
    if (!summary) return [];
    return [
        { name: 'Product total', value: summary.cartTotal },
        { name: 'Shipping', value: summary.shipping },
        { name: 'Surcharge', value: summary.surcharge },
    ].filter((r) => r.value > 0);
};

export const formatAxisEUR = (n) => {
    const v = Number(n);
    if (!Number.isFinite(v)) return '€';
    if (Math.abs(v) >= 1000) return `€${Math.round(v / 1000)}k`;
    return `€${v}`;
};

export const formatChartDateTick = (ymd) => {
    if (ymd == null || typeof ymd !== 'string') return '';
    const d = parseYmd(ymd);
    if (Number.isNaN(d.getTime())) return ymd;
    return d.toLocaleDateString('en-GB', { month: 'short', day: 'numeric', year: '2-digit' });
};

export const formatRevenueEuro = (n) => `€${formatMoney(n)}`;

export const formatPct = (n, digits = 1) => {
    const v = Number(n);
    if (!Number.isFinite(v)) return '—';
    return `${v.toFixed(digits)}%`;
};

export const formatDeltaPct = (current, prior) => {
    const c = Number(current) || 0;
    const p = Number(prior) || 0;
    if (p <= 0) return c > 0 ? '+100%' : '—';
    const pct = ((c - p) / p) * 100;
    const sign = pct > 0 ? '+' : '';
    return `${sign}${pct.toFixed(1)}%`;
};

export const trailingMean = (vals, window) => {
    const out = [];
    const w = Math.max(1, window);
    for (let i = 0; i < vals.length; i += 1) {
        const lo = Math.max(0, i - w + 1);
        let sum = 0;
        let c = 0;
        for (let j = lo; j <= i; j += 1) {
            sum += Number(vals[j]) || 0;
            c += 1;
        }
        out.push(c ? sum / c : 0);
    }
    return out;
};

export const isoWeekStart = (ymd) => {
    const d = parseYmd(ymd);
    const day = d.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    d.setDate(d.getDate() + diff);
    return formatInputDate(d);
};

export const aggregateWeekly = (chartData) => {
    const buckets = new Map();
    for (const row of chartData) {
        const weekStart = isoWeekStart(row.date);
        const cur = buckets.get(weekStart) ?? {
            weekStart,
            weekLabel: formatChartDateTick(weekStart),
            cartTotal: 0,
            shipping: 0,
            surcharge: 0,
            grossRevenue: 0,
            profit: 0,
            orderCount: 0,
        };
        cur.cartTotal += Number(row.cartTotal) || 0;
        cur.shipping += Number(row.shipping) || 0;
        cur.surcharge += Number(row.surcharge) || 0;
        cur.grossRevenue += Number(row.grossRevenue) || 0;
        cur.profit += Number(row.profit) || 0;
        cur.orderCount += Number(row.orderCount) || 0;
        buckets.set(weekStart, cur);
    }
    return [...buckets.values()].sort((a, b) => a.weekStart.localeCompare(b.weekStart));
};

/** Ignore near-zero gross days so margin % does not spike (e.g. €1 gross → 50,000%). */
export const MIN_GROSS_FOR_MARGIN_PCT = 75;

/** Display cap — fee share of gross cannot exceed 100%. */
export const MAX_MARGIN_PCT = 100;

export const dailyMarginPct = (gross, profit) => {
    const g = Number(gross) || 0;
    const p = Number(profit) || 0;
    if (g < MIN_GROSS_FOR_MARGIN_PCT) return null;
    return Math.min(MAX_MARGIN_PCT, Math.max(0, (p / g) * 100));
};

const trailingMeanNullable = (vals, window) => {
    const out = [];
    const w = Math.max(1, window);
    for (let i = 0; i < vals.length; i += 1) {
        const lo = Math.max(0, i - w + 1);
        let sum = 0;
        let c = 0;
        for (let j = lo; j <= i; j += 1) {
            const v = vals[j];
            if (v != null && Number.isFinite(v)) {
                sum += v;
                c += 1;
            }
        }
        out.push(c > 0 ? Math.min(MAX_MARGIN_PCT, sum / c) : null);
    }
    return out;
};

export const buildMarginSeries = (chartData, window = 7) => {
    const raw = chartData.map((d) =>
        dailyMarginPct(d.grossRevenue, d.profit)
    );
    const marginPctMa7 = trailingMeanNullable(raw, window);
    return chartData.map((d, i) => ({
        date: d.date,
        marginPct: raw[i],
        marginPctMa7: marginPctMa7[i],
        profit: d.profit,
        grossRevenue: d.grossRevenue,
    }));
};

/** Sensible Y-axis max for margin chart (ignores bad outliers). */
export const marginChartYMax = (marginRows, periodMarginPct) => {
    const vals = marginRows
        .map((r) => r.marginPctMa7 ?? r.marginPct)
        .filter((v) => v != null && Number.isFinite(v) && v <= MAX_MARGIN_PCT);
    const peak = vals.length ? Math.max(...vals, periodMarginPct) : periodMarginPct;
    return Math.min(MAX_MARGIN_PCT, Math.max(12, Math.ceil(peak * 1.15)));
};

export const buildOrdersSeries = (chartData, window = 7) => {
    const counts = chartData.map((d) => Number(d.orderCount) || 0);
    const ordersMa7 = trailingMean(counts, window);
    return chartData.map((d, i) => {
        const gross = Number(d.grossRevenue) || 0;
        const orders = Number(d.orderCount) || 0;
        return {
            date: d.date,
            orderCount: orders,
            ordersMa7: ordersMa7[i],
            avgOrderGross: orders > 0 ? gross / orders : null,
        };
    });
};

export const buildWeeklyComparison = (currentWeeks, priorWeeks) => {
    const n = Math.max(currentWeeks.length, priorWeeks.length);
    const rows = [];
    for (let i = 0; i < n; i += 1) {
        const cur = currentWeeks[i];
        const prev = priorWeeks[i];
        rows.push({
            periodWeek: i + 1,
            weekLabel: cur?.weekLabel ?? prev?.weekLabel ?? `W${i + 1}`,
            currentGross: cur?.grossRevenue ?? 0,
            priorGross: prev?.grossRevenue ?? 0,
            currentOrders: cur?.orderCount ?? 0,
            priorOrders: prev?.orderCount ?? 0,
        });
    }
    return rows;
};

export const computeKpis = (summary, range, priorSummary = null) => {
    const days = range ? daysInclusive(range.start, range.end) : 1;
    const gross = Number(summary?.grossRevenue) || 0;
    const profit = Number(summary?.profit) || 0;
    const orders = Number(summary?.orderCount) || 0;
    const marginPct = gross > 0 ? (profit / gross) * 100 : 0;
    const aov = orders > 0 ? gross / orders : 0;

    const priorGross = priorSummary ? Number(priorSummary.grossRevenue) || 0 : null;
    const priorOrders = priorSummary ? Number(priorSummary.orderCount) || 0 : null;

    return {
        days,
        gross,
        profit,
        orders,
        marginPct,
        aov,
        dailyAvgGross: gross / days,
        dailyAvgOrders: orders / days,
        dailyAvgProfit: profit / days,
        grossDeltaPct: priorSummary != null ? formatDeltaPct(gross, priorGross) : null,
        ordersDeltaPct: priorSummary != null ? formatDeltaPct(orders, priorOrders) : null,
    };
};
